import { Home, MessageCircle, Notebook, Plus, Sparkle } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MenuHeader } from "../../components/Menu/MenuHeader";
import { MenuOption } from "../../components/Menu/MenuOption";
import { MenuSection } from "../../components/Menu/MenuSection";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";

export interface PopupLink {
  key: string;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

/** Every built-in string, so apps can translate them. */
export interface PopupLabels {
  overview: string;
  chats: string;
  knowledge: string;
  newChat: string;
  quickActions: string;
  assistants: string;
}

export const DEFAULT_POPUP_LABELS: PopupLabels = {
  overview: "Overview",
  chats: "Chats",
  knowledge: "Knowledge",
  newChat: "New chat",
  quickActions: "Quick actions",
  assistants: "Assistants",
};

export interface PopupProps {
  spaceName: string;

  icon: ReactNode;
  onOpenOverview: () => void;
  onOpenChats: () => void;
  onOpenKnowledge: () => void;
  onNewChat: () => void;

  askLabel: string;
  onAsk: () => void;
  quickActions?: PopupLink[];
  assistants?: PopupLink[];

  children: ReactElement;

  /** Overrides for the built-in strings (see DEFAULT_POPUP_LABELS). */
  labels?: Partial<PopupLabels>;
}

function FlyoutRow({ icon, label, onClick }: Readonly<{ icon: ReactNode; label: string; onClick: () => void }>) {
  return (
    <MenuOption icon={icon} onSelect={onClick}>
      {label}
    </MenuOption>
  );
}

export function Popup({
  spaceName,
  icon,
  onOpenOverview,
  onOpenChats,
  onOpenKnowledge,
  onNewChat,
  askLabel,
  onAsk,
  quickActions,
  assistants,
  children,
  labels,
}: Readonly<PopupProps>) {
  const l = { ...DEFAULT_POPUP_LABELS, ...labels };
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuId = useId();
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef,
    placement: "right",
    align: "start",
    // Zero gap so the cursor never has to cross dead space between the trigger and the
    // panel at all; the short close delay below is just a safety net, not the main fix.
    offset: 0,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    },
    [],
  );

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const open = () => {
    clearCloseTimeout();
    setIsOpen(true);
  };
  const close = () => {
    clearCloseTimeout();
    setIsOpen(false);
  };
  // The trigger and the portaled panel are separate elements with a real gap between
  // them, so closing on the trigger's own mouseleave would unmount the panel before the
  // cursor can reach it. A short grace period lets the panel's own mouseenter cancel it.
  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 50);
  };
  // Close the flyout after a menu action so the panel does not stay open on navigation.
  const commit = (onClick: () => void) => () => {
    onClick();
    close();
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={close}
      >
        {children}
      </span>
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            id={menuId}
            aria-label={spaceName}
            style={style}
            onMouseEnter={open}
            onMouseLeave={scheduleClose}
            className="z-[110] max-h-[calc(100vh-4rem)] w-64 overflow-y-auto rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg"
          >
            <MenuHeader
              title={
                <span className="flex min-w-0 items-center gap-2">
                  <span className="shrink-0">{icon}</span>
                  <span className="truncate">{spaceName}</span>
                </span>
              }
            />
            <div className="grid grid-cols-3 gap-1 pt-1">
              {[
                { key: "overview", icon: <Home size={16} />, label: l.overview, onClick: onOpenOverview },
                { key: "chats", icon: <MessageCircle size={16} />, label: l.chats, onClick: onOpenChats },
                { key: "knowledge", icon: <Notebook size={16} />, label: l.knowledge, onClick: onOpenKnowledge },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="menuitem"
                  onClick={commit(tab.onClick)}
                  className="flex cursor-pointer flex-col items-center gap-1 rounded-lg py-2 [font:var(--type-caption)] text-content-secondary transition-colors hover:bg-surface-base hover:text-content-primary btn-focus-ring"
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
            <MenuSection divider>
              <FlyoutRow icon={<Plus size={16} />} label={l.newChat} onClick={commit(onNewChat)} />
              <FlyoutRow icon={<Sparkle size={16} />} label={askLabel} onClick={commit(onAsk)} />
            </MenuSection>
            {quickActions && quickActions.length > 0 && (
              <MenuSection title={l.quickActions} divider>
                {quickActions.map((action) => (
                  <FlyoutRow
                    key={action.key}
                    icon={action.icon}
                    label={action.label}
                    onClick={commit(action.onClick)}
                  />
                ))}
              </MenuSection>
            )}
            {assistants && assistants.length > 0 && (
              <MenuSection title={l.assistants} divider>
                {assistants.map((assistant) => (
                  <FlyoutRow
                    key={assistant.key}
                    icon={assistant.icon}
                    label={assistant.label}
                    onClick={commit(assistant.onClick)}
                  />
                ))}
              </MenuSection>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
